import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge, TruncatedText } from "@/components/ui";
import { ItemThumbnail } from "@/components/common/ItemThumbnail";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/money";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import type { Translations } from "@/types";

export interface SortableItemData {
  id: string;
  name_translations: Translations;
  description_translations: Translations | null;
  price: number;
  undiscounted_price?: number | null;
  is_visible: boolean;
  image_url?: string | null;
  serviceCategoryName?: string;
}

interface SortableItemProps {
  item: SortableItemData;
  priceColorClass: string;
  surfaceClassName?: string;
  onEdit: () => void;
  onDelete: () => void;
  onToggleVisible: () => void;
}

export function SortableItem({
  item,
  priceColorClass,
  surfaceClassName = "bg-surface-0",
  onEdit,
  onDelete,
  onToggleVisible,
}: SortableItemProps) {
  const { t, i18n } = useTranslation("components");
  const { t: tCommon } = useTranslation("common");
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const resolvedName = resolveCatalogTranslation(
    item.name_translations,
    i18n.language,
  );
  const resolvedDescription = resolveCatalogTranslation(
    item.description_translations ?? undefined,
    i18n.language,
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group flex items-center gap-3 rounded-xl border border-surface-200 p-3 transition-all",
        surfaceClassName,
        isDragging
          ? "z-50 shadow-popover ring-2 ring-primary-500/40"
          : "hover:shadow-sm",
        !item.is_visible && "opacity-60",
      )}
    >
      <button
        type="button"
        className="touch-none rounded p-0.5 text-surface-300 hover:text-surface-500 focus:outline-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 shrink-0 cursor-grab" />
      </button>

      <ItemThumbnail
        imageUrl={item.image_url}
        alt={resolvedName || t("sortableItem.itemAltFallback")}
        size="md"
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <TruncatedText className="font-medium text-surface-900">
            {resolvedName}
          </TruncatedText>
          {!item.is_visible ? (
            <>
              <EyeOff className="h-3.5 w-3.5 shrink-0 text-surface-400 sm:hidden" />
              <Badge
                variant="default"
                size="sm"
                className="hidden sm:inline-flex"
              >
                {t("sortableItem.hidden")}
              </Badge>
            </>
          ) : null}
        </div>
        {resolvedDescription && (
          <TruncatedText as="p" className="text-sm text-surface-500">
            {resolvedDescription}
          </TruncatedText>
        )}
        {item.serviceCategoryName && (
          <p className="mt-1 text-xs font-medium text-primary-600">
            {item.serviceCategoryName}
          </p>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1">
        {item.undiscounted_price != null ? (
          <>
            <Badge variant="accent" size="sm">
              {t("sortableItem.discounted")}
            </Badge>
            <span className="text-xs text-surface-400 line-through">
              {formatMoney(item.undiscounted_price)}
            </span>
            <span
              className={cn(
                "text-sm font-semibold sm:text-base",
                priceColorClass,
              )}
            >
              {formatMoney(item.price)}
            </span>
          </>
        ) : (
          <span
            className={cn(
              "text-sm font-semibold sm:text-base",
              priceColorClass,
            )}
          >
            {formatMoney(item.price)}
          </span>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
        <button
          type="button"
          onClick={onToggleVisible}
          className="rounded-lg p-1.5 text-surface-400 hover:bg-surface-100 hover:text-surface-600"
          title={
            item.is_visible ? t("sortableItem.hide") : t("sortableItem.show")
          }
        >
          {item.is_visible ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="rounded-lg p-1.5 text-surface-400 hover:bg-surface-100 hover:text-primary-600"
          title={tCommon("edit")}
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded-lg p-1.5 text-surface-400 hover:bg-error-50 hover:text-error-600"
          title={tCommon("delete")}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
